import React, { FC, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Button, ButtonIntent, ButtonSize } from 'app/design-system/components/Button';
import { Icon } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import useOutsideClickHook from 'app/lib/hooks/useOutsideClickHook';

import { Cleanup, cleanup } from '../../../../actions';
import { SubmissionState } from '../../../../constants';
import { selectSubmission } from '../../../../selectors/submission';

import dictionary from './TrashCan.dictionary.json';

import styles from './TrashCan.module.scss';

// ---

type StoreProps = {
  disabled: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<Cleanup>;
};

const TrashCan: FC<DispatchProp & StoreProps> = ({ disabled, dispatch }) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  // ---

  const closeTrashCanHandler = () => {
    setIsOpen(false);
  };

  const switchTrashCanHandler = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onCloseHandler = useCallback(() => {
    closeTrashCanHandler();
  }, []);

  const cleanupHandler = useCallback(() => {
    dispatch(cleanup());
    closeTrashCanHandler();
  }, []);

  useOutsideClickHook([buttonRef, containerRef], () => {
    closeTrashCanHandler();
  }, isOpen);

  return (
    <div data-test="trashCan-container">
      <button type="button"
              ref={buttonRef}
              disabled={disabled}
              data-test="trashCan-button"
              title={t(dictionary.buttonTitle)}
              className={styles.cleanupButton}
              data-ga-event="cart-emptyCart"
              onClick={switchTrashCanHandler}>
        <Icon icon="trash"
              data-test="trashCan-icon"
              size={16} />
      </button>
      {isOpen && (
        <div ref={containerRef}
             data-test="trashCan-tileContainer"
             className={styles.container}>
          {t(dictionary.trashCanContainerTitle)}

          <Button className={styles.button}
                  intent={ButtonIntent.PRIMARY}
                  data-test="trashCan-tileButton-no"
                  data-ga-event="cart-emptyCartConfirm"
                  data-ga-event-value="No"
                  size={ButtonSize.SMALL}
                  onClick={onCloseHandler}>
            {t(dictionary.closeText)}
          </Button>
          <Button className={styles.button}
                  intent={ButtonIntent.PRIMARY}
                  data-test="trashCan-tileButton-yes"
                  data-ga-event="cart-emptyCartConfirm"
                  data-ga-event-value="Yes"
                  size={ButtonSize.SMALL}
                  onClick={cleanupHandler}>
            {t(dictionary.cleanupText)}
          </Button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  disabled: selectSubmission(state) !== SubmissionState.AWAITING,
});

export default connect(mapStateToProps)(TrashCan);
